import React, { useState } from 'react';
import { EmailReply } from '@/hooks/useEmailReplies';
import DOMPurify from 'dompurify';
import { formatDistanceToNow } from 'date-fns';
import { Paperclip, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { EmailBodyLike, mergeEmailBody, pickBodyContent, prepareDisplayHtml } from '@/utils/emailBody';

interface EmailRepliesThreadProps {
  replies: EmailReply[];
  formatFileSize: (bytes: number) => string;
  onPreviewAttachment: (attachment: {
    id?: string;
    filename: string;
    content_type?: string;
    contentType?: string;
    size?: number;
    public_url?: string;
  }) => void;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  totalReplies: number;
  bodyFallback?: EmailBodyLike;
}

const EmailRepliesThread: React.FC<EmailRepliesThreadProps> = ({
  replies,
  formatFileSize,
  onPreviewAttachment,
  loading,
  hasMore,
  onLoadMore,
  totalReplies,
  bodyFallback,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState<{ [key: string]: boolean }>({});

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  // Function to toggle individual reply expansion
  const toggleReplyExpansion = (replyId: string) => {
    setExpandedReplies(prev => ({
      ...prev,
      [replyId]: !prev[replyId]
    }));
  };

  // Function to extract the latest reply content
  const extractLatestReply = (html: string, expanded: boolean): string => {
    const displayHtml = prepareDisplayHtml(html || '');
    if (expanded) return DOMPurify.sanitize(displayHtml);

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(displayHtml);

    // Common email reply/forward markers
    const markers = [
      '<blockquote',
      '--- Original Message ---',
      '________________________________',
      'From:',
      'Sent:',
      'To:',
      'Subject:',
      'On ',
      '> ',
      'wrote:',
    ];

    // Get all elements
    const elements = Array.from(tempDiv.children);
    let latestContent = '';
    let foundQuote = false;

    // Function to check if text contains any markers
    const containsMarker = (text: string): boolean => {
      return markers.some(marker => text.includes(marker));
    };

    // Process each element
    for (const element of elements) {
      const text = element.textContent || '';
      const html = element.outerHTML;

      // Skip empty elements
      if (!text.trim()) continue;

      // If we find a marker, break
      if (containsMarker(html)) {
        foundQuote = true;
        break;
      }

      // Add the element's HTML to our content
      latestContent += html;
    }

    // If we found a quote and have content, add a "..." indicator
    if (foundQuote && latestContent.trim()) {
      latestContent += '<div class="text-gray-500 mt-2">...</div>';
    }

    // If we didn't find any content before quotes, return a portion of the original
    if (!latestContent.trim()) {
      const cleanHtml = DOMPurify.sanitize(displayHtml);
      const firstBlockquoteIndex = cleanHtml.indexOf('<blockquote');
      if (firstBlockquoteIndex > -1) {
        latestContent = cleanHtml.substring(0, firstBlockquoteIndex) + '<div class="text-gray-500 mt-2">...</div>';
      } else {
        // If no blockquote found, return the first paragraph or div
        const tempDiv2 = document.createElement('div');
        tempDiv2.innerHTML = cleanHtml;
        const firstParagraph = tempDiv2.querySelector('p, div');
        if (firstParagraph) {
          latestContent = firstParagraph.outerHTML + '<div class="text-gray-500 mt-2">...</div>';
        } else {
          latestContent = cleanHtml;
        }
      }
    }

    return latestContent || displayHtml;
  };

  const mainReply = replies[0];
  const remainingReplies = replies.slice(1);

  if (!mainReply) return null;

  const getReplyHtml = (reply: EmailReply, isFirst: boolean) => {
    const merged = mergeEmailBody(
      reply.body,
      isFirst ? bodyFallback : null
    );
    return pickBodyContent(merged) || '';
  };

  const mainReplyHtml = getReplyHtml(mainReply, true);

  return (
    <div className="space-y-6 mt-8 p-4">
      {/* Thread Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Replies ({totalReplies})
        </h3>
      </div>

      <div className="border-l-2 border-gray-200 pl-6 space-y-8">
        {/* Main Reply */}
        <div className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
            !mainReply.flags.includes('\\Seen') 
              ? 'border-blue-200 bg-blue-50/10' 
              : 'border-gray-100'
          }`}>
          <div className="p-4 bg-gray-50 border-b border-gray-100">
            <div className="flex items-start gap-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(
                  mainReply.envelope.from[0].name || mainReply.envelope.from[0].mailbox
                )}`}
              >
                {getInitials(mainReply.envelope.from[0].name || mainReply.envelope.from[0].mailbox)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className={`text-sm font-medium ${!mainReply.flags.includes('\\Seen') ? 'text-blue-700' : 'text-gray-900'}`}>
                      {mainReply.envelope.from[0].name || `${mainReply.envelope.from[0].mailbox}@${mainReply.envelope.from[0].host}`}
                    </h3>
                    {!mainReply.flags.includes('\\Seen') && (
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(mainReply.envelope.date), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  to{' '}
                  {mainReply.envelope.to
                    .map((to) => to.name || `${to.mailbox}@${to.host}`)
                    .join(', ')}
                </p>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="prose prose-sm max-w-none">
              <div dangerouslySetInnerHTML={{
                __html: extractLatestReply(mainReplyHtml, expandedReplies[mainReply.id] || false)
              }} />
              <button
                onClick={() => toggleReplyExpansion(mainReply.id)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                {expandedReplies[mainReply.id] ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show Full Message
                  </>
                )}
              </button>
            </div>

            {mainReply.attachments && mainReply.attachments.length > 0 && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Attachments</h4>
                <div className="space-y-2">
                  {mainReply.attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => onPreviewAttachment({
                        filename: attachment.filename,
                        content_type: attachment.contentType,
                        size: attachment.size,
                        public_url: attachment.public_url,
                      })}
                    >
                      <Paperclip className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700 flex-1 truncate">
                        {attachment.filename}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatFileSize(attachment.size)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* See More Button */}
        {remainingReplies.length > 0 && (
          <div className="flex justify-center py-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Show {remainingReplies.length} More {remainingReplies.length === 1 ? 'Reply' : 'Replies'}
                </>
              )}
            </button>
          </div>
        )}

        {/* Remaining Replies */}
        {isExpanded && remainingReplies.map((reply) => (
          <div
            key={reply.id}
            className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
              !reply.flags.includes('\\Seen') 
                ? 'border-blue-200 bg-blue-50/10' 
                : 'border-gray-100'
            }`}
          >
            <div className="p-4 bg-gray-50 border-b border-gray-100">
              <div className="flex items-start gap-4">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(
                    reply.envelope.from[0].name || reply.envelope.from[0].mailbox
                  )}`}
                >
                  {getInitials(reply.envelope.from[0].name || reply.envelope.from[0].mailbox)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-sm font-medium ${!reply.flags.includes('\\Seen') ? 'text-blue-700' : 'text-gray-900'}`}>
                        {reply.envelope.from[0].name || `${reply.envelope.from[0].mailbox}@${reply.envelope.from[0].host}`}
                      </h3>
                      {!reply.flags.includes('\\Seen') && (
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(reply.envelope.date), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    to{' '}
                    {reply.envelope.to
                      .map((to) => to.name || `${to.mailbox}@${to.host}`)
                      .join(', ')}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{
                  __html: extractLatestReply(
                    getReplyHtml(reply, false),
                    expandedReplies[reply.id] || false
                  )
                }} />
                <button
                  onClick={() => toggleReplyExpansion(reply.id)}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  {expandedReplies[reply.id] ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show Full Message
                    </>
                  )}
                </button>
              </div>

              {reply.attachments && reply.attachments.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Attachments</h4>
                  <div className="space-y-2">
                    {reply.attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => onPreviewAttachment({
                        filename: attachment.filename,
                        content_type: attachment.contentType,
                        size: attachment.size,
                        public_url: attachment.public_url,
                      })}
                      >
                        <Paperclip className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700 flex-1 truncate">
                          {attachment.filename}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatFileSize(attachment.size)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {isExpanded && hasMore && (
          <div className="flex justify-center py-4">
            <button
              onClick={onLoadMore}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load More Replies'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailRepliesThread; 